import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLinks } from '../../hooks/useLinks';
import supabaseService from '../../services/supabaseService';

const ModernAnalyticsDashboard = () => {
  const { user } = useAuth();
  const { links } = useLinks();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);
  const [selectedView, setSelectedView] = useState('overview');

  useEffect(() => {
    loadAnalytics();
  }, [dateRange, links]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      console.log('📊 ModernAnalytics: Loading analytics for date range:', dateRange);
      console.log('🔗 ModernAnalytics: Available links:', (links || []).length);

      const analyticsData = await supabaseService.getUserAnalytics(null, dateRange);
      console.log('✅ ModernAnalytics: Raw analytics data:', analyticsData);

      // Calculate stats from links for more accurate numbers
      const totalClicks = (links || []).reduce((sum, link) => sum + (link.clickCount || 0), 0);
      console.log('📊 ModernAnalytics: Total clicks from links:', totalClicks);

      // Merge analytics data with link click counts
      const enhancedAnalytics = {
        ...analyticsData,
        totalClicksFromLinks: totalClicks,
        linksCount: (links || []).length
      };

      console.log('📈 ModernAnalytics: Enhanced analytics:', enhancedAnalytics);
      setAnalytics(enhancedAnalytics);
    } catch (error) {
      console.error('❌ ModernAnalytics: Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="analytics-dashboard">
        <div className="loading-state">
          <div className="spinner"></div>
          <h3>Loading analytics...</h3>
          <p>Analyzing your link performance</p>
        </div>

        <style jsx>{`
          .analytics-dashboard {
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
          }

          .loading-state {
            text-align: center;
            padding: 80px 20px;
          }

          .spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #f3f4f6;
            border-top: 4px solid #667eea;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin: 0 auto 20px;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="title-section">
            <h1>📊 Analytics Dashboard</h1>
            <p>Track performance and insights for your short links</p>
          </div>

          <div className="date-range-selector">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(parseInt(e.target.value))}
              className="range-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="overview-stats">
        <div className="stat-card">
          <div className="stat-icon">🔗</div>
          <div className="stat-content">
            <div className="stat-value">{(links || []).length}</div>
            <div className="stat-label">Total Links</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">👆</div>
          <div className="stat-content">
            <div className="stat-value">
              {(links || []).reduce((sum, link) => sum + (link.clickCount || 0), 0)}
            </div>
            <div className="stat-label">Total Clicks</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-content">
            <div className="stat-value">{analytics?.clicksToday || 0}</div>
            <div className="stat-label">Clicks Today</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-value">
              {(() => {
                const totalClicks = (links || []).reduce((sum, link) => sum + (link.clickCount || 0), 0);
                const totalLinks = (links || []).length;
                return totalLinks > 0 ? `${(totalClicks / totalLinks).toFixed(1)}` : '0';
              })()}
            </div>
            <div className="stat-label">Avg. Clicks/Link</div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="analytics-content">
        {(() => {
          const totalClicks = (links || []).reduce((sum, link) => sum + (link.clickCount || 0), 0);
          return totalClicks === 0;
        })() ? (
          <div className="no-data-state">
            <div className="no-data-icon">📈</div>
            <h3>No analytics data yet</h3>
            <p>Start sharing your links to see analytics and insights here.</p>
            <div className="suggestions">
              <h4>Tips to get started:</h4>
              <ul>
                <li>Share your short links on social media</li>
                <li>Include links in email campaigns</li>
                <li>Add QR codes to physical materials</li>
                <li>Track different marketing channels with tags</li>
              </ul>
            </div>
          </div>
        ) : (
          <>
            {/* Top Performing Links */}
            {(links || []).length > 0 && (
              <div className="analytics-section">
                <h3>🏆 Top Performing Links</h3>
                <div className="top-links-list">
                  {(links || [])
                    .filter(link => (link.clickCount || 0) > 0)
                    .sort((a, b) => (b.clickCount || 0) - (a.clickCount || 0))
                    .slice(0, 5)
                    .map((link, index) => (
                      <div key={link.id} className="top-link-item">
                        <div className="rank">#{index + 1}</div>
                        <div className="link-info">
                          <div className="link-title">{link.title || 'Untitled Link'}</div>
                          <div className="link-url">{link.shortCode}</div>
                        </div>
                        <div className="link-stats">
                          <div className="click-count">{link.clickCount || 0}</div>
                          <div className="click-label">clicks</div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Recent Activity */}
            {analytics?.clicksByDate && analytics.clicksByDate.length > 0 && (
              <div className="analytics-section">
                <h3>📅 Recent Activity</h3>
                <div className="activity-chart">
                  {analytics.clicksByDate.slice(-7).map((day, index) => (
                    <div key={index} className="activity-bar">
                      <div
                        className="bar"
                        style={{
                          height: `${Math.max(4, (day.clicks / Math.max(...analytics.clicksByDate.map(d => d.clicks))) * 100)}px`,
                          background: day.clicks > 0 ? '#667eea' : '#e5e7eb'
                        }}
                      ></div>
                      <div className="bar-label">{formatDate(day.date)}</div>
                      <div className="bar-value">{day.clicks}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Device & Geography Breakdown */}
            <div className="breakdown-sections">
              {analytics?.clicksByDevice && analytics.clicksByDevice.length > 0 && (
                <div className="analytics-section">
                  <h3>📱 Devices</h3>
                  <div className="breakdown-list">
                    {analytics.clicksByDevice.slice(0, 5).map((device, index) => (
                      <div key={index} className="breakdown-item">
                        <span className="breakdown-name">{device.name || 'Unknown'}</span>
                        <span className="breakdown-value">{device.clicks}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analytics?.clicksByReferrer && analytics.clicksByReferrer.length > 0 && (
                <div className="analytics-section">
                  <h3>🔗 Referrers</h3>
                  <div className="breakdown-list">
                    {analytics.clicksByReferrer.slice(0, 5).map((referrer, index) => (
                      <div key={index} className="breakdown-item">
                        <span className="breakdown-name">{referrer.name || 'Direct'}</span>
                        <span className="breakdown-value">{referrer.clicks}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        .analytics-dashboard {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .dashboard-header {
          margin-bottom: 32px;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
        }

        .title-section h1 {
          margin: 0 0 8px 0;
          font-size: 32px;
          color: #1a1a1a;
          font-weight: 700;
        }

        .title-section p {
          margin: 0;
          color: #6b7280;
          font-size: 16px;
        }

        .range-select {
          padding: 10px 14px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }

        .overview-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          transition: all 0.2s ease;
        }

        .stat-card:hover {
          border-color: #d1d5db;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }

        .stat-icon {
          font-size: 32px;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f9fafb;
          border-radius: 12px;
        }

        .stat-content {
          flex: 1;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 14px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
        }

        .analytics-content {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .analytics-section {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 24px;
        }

        .analytics-section h3 {
          margin: 0 0 20px 0;
          font-size: 18px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .no-data-state {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 60px 40px;
          text-align: center;
        }

        .no-data-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }

        .no-data-state h3 {
          margin: 0 0 12px 0;
          font-size: 24px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .no-data-state p {
          margin: 0 0 24px 0;
          color: #6b7280;
          font-size: 16px;
        }

        .suggestions {
          text-align: left;
          max-width: 400px;
          margin: 0 auto;
          background: #f9fafb;
          padding: 20px;
          border-radius: 12px;
        }

        .suggestions h4 {
          margin: 0 0 12px 0;
          color: #374151;
          font-size: 16px;
        }

        .suggestions ul {
          margin: 0;
          padding-left: 20px;
          color: #6b7280;
        }

        .suggestions li {
          margin-bottom: 6px;
          line-height: 1.4;
        }

        .top-links-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .top-link-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 12px;
          transition: all 0.2s ease;
        }

        .top-link-item:hover {
          background: #f3f4f6;
        }

        .rank {
          width: 32px;
          height: 32px;
          background: #667eea;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
        }

        .link-info {
          flex: 1;
          min-width: 0;
        }

        .link-title {
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 4px;
        }

        .link-url {
          font-size: 14px;
          color: #6b7280;
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .link-stats {
          text-align: right;
        }

        .click-count {
          font-size: 20px;
          font-weight: 700;
          color: #667eea;
        }

        .click-label {
          font-size: 12px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .activity-chart {
          display: flex;
          align-items: end;
          gap: 8px;
          height: 120px;
          padding: 20px 0;
        }

        .activity-bar {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .bar {
          width: 100%;
          max-width: 40px;
          background: #667eea;
          border-radius: 4px 4px 0 0;
          transition: all 0.3s ease;
        }

        .bar-label {
          font-size: 10px;
          color: #6b7280;
          text-align: center;
        }

        .bar-value {
          font-size: 12px;
          font-weight: 600;
          color: #374151;
        }

        .breakdown-sections {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .breakdown-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .breakdown-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: #f9fafb;
          border-radius: 8px;
        }

        .breakdown-name {
          color: #374151;
          font-weight: 500;
        }

        .breakdown-value {
          color: #667eea;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 16px;
          }

          .overview-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .breakdown-sections {
            grid-template-columns: 1fr;
          }

          .activity-chart {
            overflow-x: auto;
            padding-bottom: 10px;
          }
        }

        @media (max-width: 480px) {
          .overview-stats {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default ModernAnalyticsDashboard;